<template>
  <div class="editor w-100">
    <div 
      v-if="isMenubarEnabled" 
      class="editor__menubar"
      :class="{
        'editor__menubar--disabled': disabled,
        'editor__menubar--error': errorMessages.length,
        'editor__menubar--qaVariant': qaVariant,
      }"
    >
      <v-slide-x-transition>
        <div class="menubar d-flex align-center flex-wrap px-2">
          <div class="d-flex">
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('bold')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleBold"
                >
                  <app-icon-ds icon="bold" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.boldTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('italic')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleItalic"
                >
                  <app-icon-ds icon="italic" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.italicTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('strike')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleStrike"
                >
                  <app-icon-ds icon="strikethrough" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.strikeTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('underline')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleUnderline"
                >
                  <app-icon-ds icon="underline" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.underlineTooltip') }}</span>
            </AppTooltip>
            
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('highlight')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleHighlight"
                >
                  <app-icon-ds icon="highlighter" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.highlightTooltip') }}</span>
            </AppTooltip>

            <v-divider :vertical="true"
                       class="mx-2 my-auto"
                       color="black"
                       style="height: 20px"
            />

            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('bulletList')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleBulletList"
                >
                  <app-icon-ds icon="list" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.bulletListTooltip') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <app-button :class="{ 'format--active' : editor.isActive('orderedList')}"
                            :disabled="disabled"
                            type="icon"
                            size="small"
                            v-bind="props"
                            @click="toggleOrderedList"
                >
                  <app-icon-ds icon="list-ol" weight="far" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.orderedListTooltip') }}</span>
            </AppTooltip>
          </div>
          <div class="ml-auto">
            <TextCounter v-if="counter === 'menubar'"
                         :currentLength="textLength"
                         :maxLength="maxLength"
            />

            <AppTooltip v-if="sendButton">
              <template #activator="{ props }">
                <app-button type="icon"
                            size="small"
                            v-bind="props"
                            :disabled="textIsTooLong || !textLength || disabled"
                            @click="onSendClicked"
                >
                  <app-icon-ds icon="paper-plane" weight="fal" size="xs" />
                </app-button>
              </template>
              <span>{{ $t('common.visual-text-editor.VisualTextEditor.sendTooltip') }}</span>
            </AppTooltip>
          </div>
        </div>
      </v-slide-x-transition>
    </div>
    <EditorContent ref="editor"
                   :editor="editor"
                   class="editor__content bg-white"
                   :class="{
                     'editor__content-oneLine': oneLine,
                     'editor__content--error': errorMessages.length,
                     'editor__content--qaVariant': qaVariant,
                   }"
    />

    <div v-if="errorMessages.length || counter === 'footer'" class="editor__footer">
      <div v-if="errorMessages" class="editor__errors">
        <div v-for="(errorMessage, index) in errorMessages"
             :key="index"
             class="text-error text-caption"
        >
          {{ errorMessage }}
        </div>
      </div>

      <TextCounter v-if="counter === 'footer'"
                   :currentLength="textLength"
                   :label="counterLabel"
                   :maxLength="maxLength"
                   class="editor__counter"
      />
    </div>
  </div>
</template>

<script>
import { Highlight } from '@tiptap/extension-highlight'
import Mention from '@tiptap/extension-mention'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextStyle } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { StarterKit } from '@tiptap/starter-kit'
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-3'
import { cloneDeep } from 'lodash-es'
import tippy from 'tippy.js'

import AppTooltip from '@/common/AppTooltip.vue'
import TextCounter from '@/common/TextCounter.vue'
import { escapeHtml } from '@/common/utils/strings'
import MentionList from '@/common/visual-text-editor/MentionList.vue'

export default {
  name: 'VisualTextEditor',
  components: {
    AppTooltip,
    EditorContent,
    TextCounter,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    placeholder: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    taggableMembers: {
      type: Array,
    },
    taggableGroups: {
      type: Array,
    },
    oneLine: {
      type: Boolean,
      default: false,
    },
    sendButton: {
      type: Boolean,
      default: false,
    },
    hideMenubar: {
      type: Boolean,
      default: false,
    },
    cleanContentOnValidation: {
      type: Boolean,
      default: false,
    },
    cleanContentOnBlur: {
      type: Boolean,
      default: true,
    },
    keepToolbarOnBlur: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      required: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    counter: {
      validator (value) {
        return ['menubar', 'footer'].includes(value)
      },
      required: false,
    },
    counterLabel: {
      type: String,
      default: '',
    },
    qaVariant: {
      type: Boolean,
      default: false,
    },
    cleanHtmlOnInput: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
    },
  },
  emits: ['update:value', 'input', 'blur', 'focus', 'sendClicked'],
  data () {
    return {
      editor: null,
      editorIsFocused: false,
      pickedHighlightColor: '#FFE066',
      items: [],
      tippyPopup: null,
    }
  },
  computed: {
    isEditable() {
      return !this.disabled && !this.readOnly
    },
    isMenubarEnabled () {
      return this.editor && !this.hideMenubar && !this.readOnly
    },
    mentionable () {
      const array = []
      if (this.taggableGroups) {
        const groups = cloneDeep(this.taggableGroups)
        groups.sort((a, b) => {
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        })
        array.push(...groups)
      }
      if (this.taggableMembers && this.taggableGroups) {
        array.push({ name: 'separator' })
        const members = cloneDeep(this.taggableMembers)
        members.sort((a, b) => {
          return a.fullName.localeCompare(b.fullName, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        })
        array.push(...members)
      } else if (this.taggableMembers) {
        const members = cloneDeep(this.taggableMembers)
        members.sort((a, b) => {
          return a.fullName.localeCompare(b.fullName, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        })
        array.push(...members)
        array.push(...this.taggableMembers)
      }
      return array
    },
    textLength () {
      return escapeHtml(this.value).length
    },
    textIsTooLong () {
      return this.maxLength && this.textLength > this.maxLength
    },
    formattedValue () {
      // Format mentions spans and add a space before the end of paragraph
      const formattedMention = this.value?.replaceAll('data-mention', 'data-type="mention"')
      if (formattedMention?.length) {
        const lastClosingTagIndex = formattedMention.lastIndexOf('</')
        if (lastClosingTagIndex !== -1) {
          return formattedMention.slice(0, lastClosingTagIndex) + ' ' + formattedMention.slice(lastClosingTagIndex)
        }
      }
      return formattedMention
    },
  },
  beforeUnmount () {
    this.editor.destroy()
  },
  mounted () {
    this.editor = new Editor({
      extensions: [
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          suggestion: {
            items: editor => {
              return this.mentionable.filter(item => {
                if (item.fullName) {
                  if (item.fullName.toLowerCase().includes(editor.query.toLowerCase())) {
                    return item
                  }
                } else if (item.name && item.name !== 'separator') {
                  if (item.name.toLowerCase().includes(editor.query.toLowerCase())) {
                    return item
                  }
                }
              })
            },
            render: () => {
              let component
              let popup
              return {
                onStart: props => {
                  component = new VueRenderer(MentionList, {
                    props: props,
                    editor: this.editor,
                  })
                  popup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                  })
                  this.tippyPopup = popup
                },
                onUpdate (props) {
                  component.updateProps(props)
                  popup[0].setProps({
                    getReferenceClientRect: props.clientRect,
                  })
                },
                onKeyDown (props) {
                  if (props.event.key === 'Escape') {
                    popup[0].hide()
                    return true
                  }
                  return component.ref?.onKeyDown(props)
                },
                onExit () {
                  popup[0].destroy()
                  component.destroy()
                },
              }
            },
          },
        }),
        StarterKit,
        Underline,
        TextStyle,
        Highlight,
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
      ],
      editorProps: { handleDOMEvents: {
        drop: (_, e) => { e.preventDefault(); },
      } },
      editable: this.isEditable,
      parseOptions: {
        preserveWhitespace: 'full',
      },
      injectCSS: false,
      content: this.formattedValue,
      onUpdate: () => {
        const content = this.editor.getHTML()
        const finalContent = this.cleanHtmlOnInput ? this.cleanHtml(content) : content
        this.$emit('input', finalContent)
        this.$emit('update:value', finalContent)
      },
      onBlur: ({ event }) => {
        if (event.relatedTarget) {
          if (!event.relatedTarget.classList.contains('blur-editor-exception') && !event.relatedTarget.classList.contains('tippy-box')) {
            if (this.tippyPopup) {
              this.tippyPopup[0].destroy()
            }
            if (this.cleanContentOnBlur || !this.keepToolbarOnBlur || this.value === '<p></p>') {
              this.editorIsFocused = false
            }
            this.$emit('blur')
            if (this.cleanContentOnValidation && this.cleanContentOnBlur) {
              this.editor.commands.clearContent()
            } else if (!this.keepToolbarOnBlur || this.value === '<p></p>') {
              this.editorIsFocused = false
            }
          }
        } else {
          this.$emit('blur')
          if (!this.keepToolbarOnBlur || this.value === '<p></p>') {
            this.editorIsFocused = false
          }
          if (this.cleanContentOnValidation && this.cleanContentOnBlur) {
            this.editor.commands.clearContent()
          }
          if (this.tippyPopup) {
            this.tippyPopup[0].destroy()
          }
        }
      },
      onFocus: () => {
        this.editorIsFocused = true
        this.$emit('focus')
      },
    })
  },
  methods: {
    toggleBold () { this.editor.chain().focus().toggleBold().run() },
    toggleItalic () { this.editor.chain().focus().toggleItalic().run() },
    toggleStrike () { this.editor.chain().focus().toggleStrike().run() },
    toggleUnderline () { this.editor.chain().focus().toggleUnderline().run() },
    toggleBulletList () { this.editor.chain().focus().toggleBulletList().run() },
    toggleOrderedList () { this.editor.chain().focus().toggleOrderedList().run() },
    clearFormat () { this.editor.chain().focus().clearNodes().unsetAllMarks().run() },
    toggleHighlight () { this.editor.chain().focus().toggleHighlight().run() },
    onOpenMentionList () {
      this.editor.commands.insertContent('@', { emitUpdate: true });
    },
    onSendClicked () {
      this.$emit('sendClicked')
      if (this.cleanContentOnValidation) {
        this.editor.commands.clearContent()
        this.editorIsFocused = false
      }
    },
    focus () {
      this.editor.commands.focus('end')
    },
    clear () {
      this.editor.commands.clearContent()
    },
    cleanHtml (value) {
      if (!value) return ''
      return value.replace(/<p>\s*<\/p>/g, '').trim() // Remove empty paragraphs
    },
  },
  watch: {
    isEditable (newValue) {
      if (this.editor) {
        this.editor.setEditable(newValue)
      }
    },
    value (newValue) {
      if (this.editor && this.editor.getHTML() !== newValue) {
        this.editor.commands.setContent(newValue, false)
      }
    },
  },
}
</script>

<style lang="scss">
.editor {
  &__menubar {
    padding-left: 10px;
    padding-right: 10px;
    background-color: rgb(var(--v-theme-grey-lighten-6));
    border: 1px solid rgb(var(--v-theme-grey-300));
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    transition: opacity 0.1s ease-in-out;

    &--qaVariant {
      border-left-color: rgb(var(--v-theme-accent-darken-3));
      border-right-color: rgb(var(--v-theme-accent-darken-3));
      border-top-color: rgb(var(--v-theme-accent-darken-3));
      border-bottom-color: transparent;
    }

    &-icon {
      font-size: 12px;
      color: rgb(var(--v-theme-accent-lighten-2));
    }

    &--disabled {
      opacity: 0.5;
    }

    &--error {
      border-top: 1px solid rgb(var(--v-theme-error));
      border-left: 1px solid rgb(var(--v-theme-error));
      border-right: 1px solid rgb(var(--v-theme-error));
    }
  }

  &__content {
    .ProseMirror {
      min-height: 75px;
      max-height: 400px;
      overflow-y: auto;
      padding: 12px;
      transition: all 0.2s;
      border: 1px solid #ddd;
      border-bottom-left-radius: 3px;
      border-bottom-right-radius: 3px;
      font-size: 14px;
      outline: none !important;
      &-focused {
        outline: none;
        border-color: rgb(var(--v-theme-tertiary-lighten-3));
      }
      .mention {
        color: rgb(var(--v-theme-info-darken-1));
      }
      * {
        white-space: pre-wrap;
        word-wrap: break-word;
      }

      ol, ul {
        padding-left: 16px;
      }
    }

    &-oneLine {
      .ProseMirror {
        min-height: 17px;
      }
    }

    &--qaVariant {
      .ProseMirror {
        border-left-color: rgb(var(--v-theme-accent-darken-3));
        border-right-color: rgb(var(--v-theme-accent-darken-3));
        border-bottom-color: rgb(var(--v-theme-accent-darken-3));
        border-top-color: transparent;
      }
    }

    &--error {
      .ProseMirror {
        border-bottom: 1px solid rgb(var(--v-theme-error));
        border-left: 1px solid rgb(var(--v-theme-error));
        border-right: 1px solid rgb(var(--v-theme-error));
      }
    }
    
    .is-read-only & {
      .ProseMirror {
        background-color: rgb(var(--v-theme-grey-50));
        border: 1px solid rgb(var(--v-theme-grey-200));
        color: rgb(var(--v-theme-grey-800)) !important;
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
  }

  &__errors {
    flex: 1;
  }

  &__counter {
    flex: 1;
    text-align: right;
  }
}

.padding-prose-mirror .ProseMirror {
  padding: 10px;
}

.ProseMirror code {
  background-color: rgb(255, 255, 0);
  padding: 0px;
  font-size: 100%;
  color: rgb(var(--v-theme-accent));
  font-weight: initial;
  border-radius: 0px;
  font-family: 'Roboto', sans-serif;
}

.menubar {
  color: rgb(var(--v-theme-accent-lighten-4));

  .send-button:disabled {
    .editor__menubar-icon {
      color: rgb(var(--v-theme-accent-lighten-4));
    }

    .v-btn__overlay {
      opacity: 0;
    }
  }
  
  .format--active {
    background-color: rgb(var(--v-theme-grey-200)) !important;
  }
}

.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: rgb(var(--v-theme-grey-500));
  font-size: 14px;
  pointer-events: none;
  height: 0;
}
</style>
